'use client';

import { useState } from 'react';

import { deleteCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import { ConfirmDialog } from '@eliadmin/core/src/components/nt/account/profile/confirm-dialog';
import { useAccountTexts } from '@eliadmin/core/src/components/nt/shared/site-texts';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

type DeleteStatus = 'idle' | 'deleting' | 'failed';

/**
 * Figma „Zrušiť konto“ (205:11129): Body/M #5D655D, underlined, at the end of the actions row. It
 * asks first (core „Naozaj chcete vymazať účet?“ / „Vymazať účet“); after `deleteCustomer` a full
 * navigation to the core sign-out route drops the session cookie.
 */
export function DeleteAccount({ authToken }: { authToken: string }) {
  const { t } = useTranslations();
  const texts = useAccountTexts();
  const locale = useLocaleStore(s => s.currentLocale);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<DeleteStatus>('idle');
  if (!texts.deleteAccount) return null;

  const remove = async () => {
    setStatus('deleting');
    try {
      await deleteCustomer({ locale, authToken });
      window.location.assign(ROUTES.SIGN_OUT());
    } catch {
      setStatus('failed');
    }
  };

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        className="nt-body-m shrink-0 self-start rounded text-[#5D655D] underline underline-offset-2 transition-colors hover:text-[#0D110D] focus-visible:shadow-focus focus-visible:outline-none @[36rem]:self-auto"
      >
        {texts.deleteAccount}
      </button>
      <ConfirmDialog
        open={open}
        onOpenChange={next => {
          setOpen(next);
          setStatus('idle');
        }}
        title={texts.deleteAccount}
        description={t('text2090')}
        error={status === 'failed' ? t('text1320') : null}
        busy={status === 'deleting'}
        confirm={
          <Button
            type="button"
            variant="destructive"
            onClick={remove}
            isLoading={status === 'deleting'}
          >
            {t('text755')}
          </Button>
        }
      />
    </>
  );
}
